import { useEditor, EditorContent, Editor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Mention from "@tiptap/extension-mention";
import Placeholder from "@tiptap/extension-placeholder";
import TaskList from "@tiptap/extension-task-list";
import TaskItem from "@tiptap/extension-task-item";
import Heading from "@tiptap/extension-heading";
import CodeBlock from "@tiptap/extension-code-block";
import Blockquote from "@tiptap/extension-blockquote";
import HorizontalRule from "@tiptap/extension-horizontal-rule";
import type {
  SuggestionKeyDownProps,
  SuggestionProps,
} from "@tiptap/suggestion";
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  useCallback,
} from "react";
import { useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";
import {
  Bold,
  Italic,
  List,
  ListOrdered,
  Heading1,
  Heading2,
  Heading3,
  Quote,
  Code,
  CheckSquare,
  Minus,
  Strikethrough,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { usePeople } from "@/hooks/usePeople";
import { useSlashCommandItems } from "./useSlashCommandItems";
import { createSlashCommandSuggestion } from "./slashCommandSuggestion";
import { ExternalLinkDialog } from "./ExternalLinkDialog";

interface RichTextEditorProps {
  content?: string;
  onChange?: (html: string, mentionedPersonIds: string[]) => void;
  placeholder?: string;
  className?: string;
  editable?: boolean;
}

export interface RichTextEditorRef {
  getHTML: () => string;
  getMentionedPersonIds: () => string[];
  setContent: (content: string) => void;
}

interface MentionItem {
  id: string;
  label: string;
}

type MentionRenderProps = {
  lifecycle: SuggestionProps<MentionItem, MentionItem>;
  keyDown: SuggestionKeyDownProps;
};

const MenuBar = ({ editor }: { editor: Editor | null }) => {
  if (!editor) return null;

  return (
    <div className="flex items-center gap-0.5 p-2 border-b border-border flex-wrap">
      {/* Text formatting */}
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleBold().run()}
        className={cn("h-8 w-8 p-0", editor.isActive("bold") && "bg-accent")}
        title="Bold"
      >
        <Bold className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleItalic().run()}
        className={cn("h-8 w-8 p-0", editor.isActive("italic") && "bg-accent")}
        title="Italic"
      >
        <Italic className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleStrike().run()}
        className={cn("h-8 w-8 p-0", editor.isActive("strike") && "bg-accent")}
        title="Strikethrough"
      >
        <Strikethrough className="h-4 w-4" />
      </Button>

      <Separator orientation="vertical" className="mx-1 h-6" />

      {/* Headings */}
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
        className={cn(
          "h-8 w-8 p-0",
          editor.isActive("heading", { level: 1 }) && "bg-accent",
        )}
        title="Heading 1"
      >
        <Heading1 className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
        className={cn(
          "h-8 w-8 p-0",
          editor.isActive("heading", { level: 2 }) && "bg-accent",
        )}
        title="Heading 2"
      >
        <Heading2 className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
        className={cn(
          "h-8 w-8 p-0",
          editor.isActive("heading", { level: 3 }) && "bg-accent",
        )}
        title="Heading 3"
      >
        <Heading3 className="h-4 w-4" />
      </Button>

      <Separator orientation="vertical" className="mx-1 h-6" />

      {/* Lists */}
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        className={cn(
          "h-8 w-8 p-0",
          editor.isActive("bulletList") && "bg-accent",
        )}
        title="Bullet list"
      >
        <List className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        className={cn(
          "h-8 w-8 p-0",
          editor.isActive("orderedList") && "bg-accent",
        )}
        title="Numbered list"
      >
        <ListOrdered className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleTaskList().run()}
        className={cn(
          "h-8 w-8 p-0",
          editor.isActive("taskList") && "bg-accent",
        )}
        title="Task list"
      >
        <CheckSquare className="h-4 w-4" />
      </Button>

      <Separator orientation="vertical" className="mx-1 h-6" />

      {/* Blocks */}
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleBlockquote().run()}
        className={cn(
          "h-8 w-8 p-0",
          editor.isActive("blockquote") && "bg-accent",
        )}
        title="Quote"
      >
        <Quote className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleCodeBlock().run()}
        className={cn(
          "h-8 w-8 p-0",
          editor.isActive("codeBlock") && "bg-accent",
        )}
        title="Code block"
      >
        <Code className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().setHorizontalRule().run()}
        title="Horizontal rule"
      >
        <Minus className="h-4 w-4" />
      </Button>
    </div>
  );
};

export const RichTextEditor = forwardRef<
  RichTextEditorRef,
  RichTextEditorProps
>(
  (
    {
      content = "",
      onChange,
      placeholder = "Type @ to mention people, / for commands...",
      className,
      editable = true,
    },
    ref,
  ) => {
    const navigate = useNavigate();
    const { data: people = [] } = usePeople("employee");
    const peopleRef = useRef(people);
    useEffect(() => {
      peopleRef.current = people;
    }, [people]);
    const [mentionedPersonIds, setMentionedPersonIds] = useState<string[]>([]);

    // Slash command state
    const { itemsRef: slashItemsRef } = useSlashCommandItems();
    const [externalLinkDialog, setExternalLinkDialog] = useState<{
      open: boolean;
      position: { top: number; left: number };
    }>({ open: false, position: { top: 0, left: 0 } });
    const editorRef = useRef<Editor | null>(null);

    const handleExternalLink = useCallback(() => {
      // Position the dialog near the cursor
      const sel = window.getSelection();
      if (sel && sel.rangeCount > 0) {
        const range = sel.getRangeAt(0);
        const rect = range.getBoundingClientRect();
        setExternalLinkDialog({
          open: true,
          position: { top: rect.bottom + 4, left: rect.left },
        });
      } else {
        setExternalLinkDialog({
          open: true,
          position: { top: 200, left: 200 },
        });
      }
    }, []);

    const handleExternalLinkSubmit = useCallback(
      (url: string, label: string) => {
        const ed = editorRef.current;
        if (!ed) return;
        ed.chain()
          .focus()
          .insertContent({
            type: "text",
            text: label,
            marks: [
              {
                type: "link",
                attrs: {
                  href: url,
                  target: "_blank",
                  rel: "noopener noreferrer",
                },
              },
            ],
          })
          .insertContent({ type: "text", text: " " })
          .run();
        setExternalLinkDialog({ open: false, position: { top: 0, left: 0 } });
      },
      [],
    );

    const handleExternalLinkCancel = useCallback(() => {
      setExternalLinkDialog({ open: false, position: { top: 0, left: 0 } });
      editorRef.current?.chain().focus().run();
    }, []);

    const handleEditorClick = useCallback(
      (e: React.MouseEvent) => {
        const link = (e.target as HTMLElement).closest("a");
        if (!link) return;
        e.preventDefault();
        const href = link.getAttribute("href");
        if (href?.startsWith("/")) {
          navigate(href);
        } else if (href) {
          window.open(href, "_blank", "noopener,noreferrer");
        }
      },
      [navigate],
    );

    const extractMentions = useCallback((editor: Editor) => {
      const mentions: string[] = [];
      editor.state.doc.descendants((node) => {
        if (node.type.name === "mention" && node.attrs.id) {
          mentions.push(node.attrs.id);
        }
      });
      return [...new Set(mentions)];
    }, []);

    const editor = useEditor({
      extensions: [
        StarterKit.configure({
          heading: false,
          codeBlock: false,
          blockquote: false,
          horizontalRule: false,
          link: {
            openOnClick: false,
            HTMLAttributes: {
              class: "text-primary underline cursor-pointer",
            },
          },
        }),
        Heading.configure({
          levels: [1, 2, 3],
        }),
        CodeBlock.configure({
          HTMLAttributes: {
            class: "bg-muted rounded-md p-4 font-mono text-sm",
          },
        }),
        Blockquote.configure({
          HTMLAttributes: {
            class:
              "border-l-4 border-primary/30 pl-4 italic text-muted-foreground",
          },
        }),
        HorizontalRule.configure({
          HTMLAttributes: {
            class: "my-4 border-t border-border",
          },
        }),
        TaskList.configure({
          HTMLAttributes: {
            class: "not-prose pl-0",
          },
        }),
        TaskItem.configure({
          HTMLAttributes: {
            class: "flex items-start gap-2",
          },
          nested: true,
        }),
        Placeholder.configure({
          placeholder,
          emptyEditorClass:
            "before:content-[attr(data-placeholder)] before:text-muted-foreground before:float-left before:pointer-events-none before:h-0",
        }),
        Mention.configure({
          HTMLAttributes: {
            class: "mention bg-primary/20 text-primary rounded px-1 py-0.5",
          },
          suggestions: [
            {
              char: "@",
              items: ({ query }: { query: string }) => {
                return peopleRef.current
                  .filter((person) =>
                    `${person.first_name} ${person.last_name}`
                      .toLowerCase()
                      .includes(query.toLowerCase()),
                  )
                  .slice(0, 5)
                  .map((person) => ({
                    id: person.id,
                    label: `${person.first_name} ${person.last_name}`,
                  }));
              },
              render: () => {
                let component: HTMLDivElement | null = null;
                let selectedIndex = 0;
                let items: MentionItem[] = [];
                let executeCommand: ((item: MentionItem) => void) | null = null;

                const updateContent = () => {
                  if (!component) return;
                  component.innerHTML =
                    items.length === 0
                      ? '<div class="px-2 py-1 text-muted-foreground text-sm">No results</div>'
                      : items
                          .map(
                            (item: MentionItem, index: number) =>
                              `<div class="px-2 py-1 rounded cursor-pointer text-sm ${
                                index === selectedIndex
                                  ? "bg-accent"
                                  : "hover:bg-accent/50"
                              }" data-index="${index}">${item.label}</div>`,
                          )
                          .join("");
                };

                return {
                  onStart: (props: MentionRenderProps["lifecycle"]) => {
                    executeCommand = props.command;
                    items = props.items;
                    selectedIndex = 0;

                    component = document.createElement("div");
                    component.className =
                      "bg-popover border border-border rounded-md shadow-lg p-1 z-50";

                    updateContent();

                    component.addEventListener("click", (e) => {
                      const target = e.target as HTMLElement;
                      const index = target.dataset.index;
                      if (index !== undefined) {
                        props.command(items[parseInt(index)]);
                      }
                    });

                    const rect = props.clientRect?.();
                    if (rect && component) {
                      component.style.position = "fixed";
                      component.style.left = `${rect.left}px`;
                      component.style.top = `${rect.bottom + 4}px`;
                      document.body.appendChild(component);
                    }
                  },
                  onUpdate: (props: MentionRenderProps["lifecycle"]) => {
                    executeCommand = props.command;
                    items = props.items;
                    selectedIndex = 0;
                    updateContent();
                  },
                  onKeyDown: (props: MentionRenderProps["keyDown"]) => {
                    if (props.event.key === "ArrowUp") {
                      selectedIndex =
                        (selectedIndex - 1 + items.length) % items.length;
                      updateContent();
                      return true;
                    }
                    if (props.event.key === "ArrowDown") {
                      selectedIndex = (selectedIndex + 1) % items.length;
                      updateContent();
                      return true;
                    }
                    if (props.event.key === "Enter") {
                      if (items[selectedIndex]) {
                        executeCommand?.(items[selectedIndex]);
                      }
                      return true;
                    }
                    return false;
                  },
                  onExit: () => {
                    if (component) {
                      component.remove();
                      component = null;
                    }
                    executeCommand = null;
                  },
                };
              },
            },
            createSlashCommandSuggestion(slashItemsRef, handleExternalLink),
          ],
        }),
      ],
      content,
      editable,
      editorProps: {
        attributes: {
          class: cn(
            "prose prose-sm prose-invert max-w-none focus:outline-none min-h-[150px] p-3",
            "prose-p:my-1 prose-ul:my-1 prose-ol:my-1",
            "prose-h1:text-xl prose-h1:font-bold prose-h1:my-2",
            "prose-h2:text-lg prose-h2:font-semibold prose-h2:my-2",
            "prose-h3:text-base prose-h3:font-medium prose-h3:my-1",
            "[&_.mention]:bg-primary/20 [&_.mention]:text-primary [&_.mention]:rounded [&_.mention]:px-1 [&_.mention]:py-0.5",
            "[&_ul[data-type=taskList]]:list-none [&_ul[data-type=taskList]]:pl-0",
            "[&_ul[data-type=taskList]_li]:flex [&_ul[data-type=taskList]_li]:items-start [&_ul[data-type=taskList]_li]:gap-2",
            "[&_ul[data-type=taskList]_input]:mt-1",
            "[&_a]:text-primary [&_a]:underline [&_a]:cursor-pointer",
          ),
          "data-gramm": "false",
          "data-gramm_editor": "false",
          "data-enable-grammarly": "false",
          inputmode: "text",
          enterkeyhint: "enter",
          autocomplete: "off",
          autocorrect: "on",
          autocapitalize: "sentences",
          spellcheck: "true",
          contenteditable: "true",
        },
      },
      onUpdate: ({ editor }) => {
        const html = editor.getHTML();
        const mentions = extractMentions(editor);
        setMentionedPersonIds(mentions);
        onChange?.(html, mentions);
      },
    });

    useEffect(() => {
      editorRef.current = editor;
    }, [editor]);

    useEffect(() => {
      if (editor && content !== editor.getHTML()) {
        editor.commands.setContent(content);
      }
    }, [content, editor]);

    useImperativeHandle(ref, () => ({
      getHTML: () => editor?.getHTML() || "",
      getMentionedPersonIds: () => mentionedPersonIds,
      setContent: (newContent: string) => {
        editor?.commands.setContent(newContent);
      },
    }));

    return (
      <>
        <div
          className={cn(
            "border border-input rounded-md bg-background",
            className,
          )}
          onClick={handleEditorClick}
        >
          {editable && <MenuBar editor={editor} />}
          <EditorContent editor={editor} />
        </div>
        <ExternalLinkDialog
          open={externalLinkDialog.open}
          position={externalLinkDialog.position}
          onSubmit={handleExternalLinkSubmit}
          onCancel={handleExternalLinkCancel}
        />
      </>
    );
  },
);

RichTextEditor.displayName = "RichTextEditor";
